<script setup lang="ts">
import { computed, ref } from 'vue'
import type { TypesEdgesSumFieldType } from './../../model/types.ts'
import { useHelpers, useTranslate } from '@/shared/lib'
import { useConfirm, VIcons } from '@/shared/ui'
import {
  InventTypeField,
  InventTypeFieldSetting,
  TypeFieldList,
} from '@/entities/Inventory/TypeField'
import { useTypesEdgesModel } from '@/features/Inventory/ObjectTypesEdges/model/model.ts'

const { trs } = useTranslate()
const { generateKey } = useHelpers()
const { show } = useConfirm()

const model = useTypesEdgesModel()

/** Шапка таблицы */
const headers = [
  { title: trs('itf.name_label'), key: 'name', sortable: false, width: '30%' },
  {
    title: trs('itf.type_field'),
    key: 'type',
    sortable: false,
    cellProps: { class: 'flex-center__button' },
    headerProps: { class: 'flex-center__button' },
  },
  { title: trs('itf.default_value'), key: 'default_value', sortable: false, width: '30%' },
  { title: trs('itf.grid'), key: 'grid', sortable: false, width: '7%' },
  { title: trs('itf.action'), key: 'action', sortable: false, width: '3%' },
]

/**
 * Функция удаления объекта
 * @param id
 */
const deleteHandler = (id: string) => {
  show({
    title: trs('itf.delete_field'),
    confirmFunc: () => {
      if (!model.item) return

      model.item.sum_fields = model.item.sum_fields.filter((item) => item.id !== id)
    },
  })
}

/** Вычисляем список */
const filedTypes = computed(() => {
  const list: any[] = []

  TypeFieldList.forEach((item) => {
    if (item.name !== 'Дополнительные') return

    list.push({ type: 'subheader', title: item.name })

    item.children.forEach((child) => {
      list.push({ title: child.name, value: child.key })
    })
  })

  return list
})

/**
 * Функция добавления поля сводной информации
 */
const addHandler = () => {
  if (!model.item) return

  model.item.sum_fields.push({
    id: generateKey(),
    name: '',
    type: 'computed',
    default_value: '',
    grid: 4,
    computed_settings: {
      list_id: 0,
      condition_value: null,
      computed_type_id: '',
      current_field_id: '',
      optional_field_id: null,
      computed_condition_id: null,
      condition_value_range: { max: 0, min: 0 },
      condition_value_in_list: null,
    },
  })
}

const openSettings = ref(false)
const fieldSettings = ref<any>(null)

/**
 * Функция открытия окна настроек поля
 * @param item
 */
const openSettingsHandler = (item: TypesEdgesSumFieldType) => {
  openSettings.value = true
  fieldSettings.value = item
}
</script>

<template>
  <VDataTable
    v-if="model.item"
    :headers
    :items="model.item.sum_fields"
    items-per-page="1000"
    hide-default-footer
  >
    <template #item.name="{ item }">
      <VTextField v-model="item.name" hide-details />
    </template>

    <template #item.type="{ item }">
      <VSelect :items="filedTypes" v-model="item.type" hide-details />

      <VBtn @click="openSettingsHandler(item)">
        <VIcons type="tune" />
      </VBtn>
    </template>

    <template #item.default_value="{ item }">
      <InventTypeField
        :label="$t('itf.default_value')"
        :field-data="item"
        v-bind="{ ...item }"
        hide-details
        v-model="item.default_value"
      />
    </template>

    <template #item.grid="{ item }">
      <VTextField v-model="item.grid" hide-details />
    </template>

    <template #item.action="{ item }">
      <VBtn icon density="compact" color="error" @click="deleteHandler(item.id)">
        <VIcons type="delete" />
      </VBtn>
    </template>
  </VDataTable>

  <div class="table__btn_left mt-2">
    <VBtn color="default" variant="tonal" @click="addHandler">{{ $t('itf.add') }}</VBtn>
  </div>

  <InventTypeFieldSetting
    v-if="fieldSettings"
    v-model="openSettings"
    v-model:field="fieldSettings"
  />
</template>
